import { useState } from 'react';
import PropTypes from 'prop-types';
import { postJson } from '../global/func';
import logo from '../assets/images/logo-mark.png';

// The alphabet generateClaimCode() draws from, server side. Anything outside it
// cannot be a real code, so it is stripped as the customer types rather than
// rejected after they submit.
const CODE_ALPHABET = /[^23456789ABCDEFGHJKMNPQRSTUVWXYZ]/g;
const CODE_LENGTH = 8;

const ClaimDevice = ({ User, onClaimed, onCancel, isFirstDevice }) => {

    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Held rather than handed straight to onClaimed. Claiming makes the account
    // own a device, so the parent immediately renders the settings screen and
    // unmounts this one -- taking the confirmation with it before anyone can
    // read it. The customer saw the page change and nothing else.
    const [claimed, setClaimed] = useState(null);

    const codeChanged = (raw) => {
        // Codes never contain O, 0, I, 1 or L. Anything outside the alphabet is
        // dropped as it is typed, which shows up immediately in the character
        // count. Guessing what a mistyped character "meant" is not possible --
        // a 0 could stand for D or Q -- so silently substituting one would just
        // turn a visible mistake into an invisible one.
        const cleaned = raw
            .toUpperCase()
            .replace(CODE_ALPHABET, '')
            .slice(0, CODE_LENGTH);

        setCode(cleaned);
        if (error) setError('');
    }

    const submit = async (e) => {
        e.preventDefault();

        if (code.length !== CODE_LENGTH) {
            setError(`Enter all ${CODE_LENGTH} characters shown on your Prayer Box screen.`);
            return;
        }

        setBusy(true);
        setError('');

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/claim/');
        const auth = { USER_ID: User.id, AUTH_KEY: User.auth_key };
        const result = await postJson(url, { claimCode: code, ...auth }, auth);

        setBusy(false);

        if (result.ok) {
            setSuccess('Linked. Your Prayer Box will show prayer times within a minute.');
            setClaimed(result.data ?? {});
            return;
        }

        if (result.status === 0) {
            setError('Could not reach the server. Check your connection and try again.');
        } else if (result.status === 401) {
            setError('Your session expired. Please sign in again.');
        } else {
            setError(result.data?.error || 'Could not link that device. Please try again.');
        }
    }

    return (
        <div className="column auth">
            <img className="auth__logo" src={logo} alt="Prayer Box" />
            <h1 className="auth__title">
                {isFirstDevice ? 'Add your Prayer Box' : 'Link another Prayer Box'}
            </h1>
            <p className="auth__sub">
                Your Prayer Box shows an eight-character code on the television once it is
                connected to Wi-Fi. Enter that code to add it to your account.
            </p>

            <form onSubmit={submit}>
                <div className="field">
                    <label className="field__label" htmlFor="claim-code">Code from your screen</label>
                    <input
                        id="claim-code"
                        name="claim-code"
                        type="text"
                        className="claim-code"
                        value={code}
                        onChange={(e) => codeChanged(e.currentTarget.value)}
                        placeholder="--------"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck="false"
                        disabled={busy || !!success}
                        aria-describedby="claim-code-help" />
                </div>
                <div id="claim-code-help" className="muted" style={{ textAlign: 'center', marginBottom: '.7rem' }}>
                    {code.length}/{CODE_LENGTH} characters
                </div>

                {error && <div className="notice notice--warn" role="alert">{error}</div>}
                {success && <div className="notice notice--info" role="status">{success}</div>}

                {success
                    ? <button type="button" className="btn btn--primary btn--block"
                              onClick={() => onClaimed(claimed)}>
                        Continue
                      </button>
                    : <button type="submit" className="btn btn--primary btn--block"
                              disabled={busy || code.length !== CODE_LENGTH}>
                        {busy ? <><span className="spinner" />Linking…</> : 'Link this Prayer Box'}
                      </button>}
            </form>

            {onCancel && !success &&
                <button type="button" className="btn btn--block" style={{ marginTop: '.5rem' }}
                        onClick={onCancel}>
                    Cancel
                </button>}

            <p className="hint">
                No code on screen? The Prayer Box needs Wi-Fi first. If it is showing setup
                instructions, follow those, and the code will appear once it is online.
            </p>
        </div>
    )
}

ClaimDevice.propTypes = {
    User: PropTypes.object.isRequired,
    onClaimed: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    isFirstDevice: PropTypes.bool,
}

export default ClaimDevice;
